// TRANK — materiálové úkoly. Jedna obrazovka pro obě strany kyvadla:
//
//   „Brno — naložit materiál"    řidič skenuje pečeti, kusy postupně zelenají
//   „Praha — vyložit materiál"   jedno potvrzení „všechno sedí"; kus, který v bedně
//                                není, se ťuknutím označí jako Pohřešuje se
//
// Vyložení na depu NAHRAZUJE prijem.routera.cz: potvrzení, že materiál fyzicky dojel,
// dělá řidič, který ho přivezl. Teprve tím ho místní řidič uvidí ve své mobilce.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IonBackButton, IonButtons, IonContent, IonHeader, IonPage,
  IonSpinner, IonTitle, IonToolbar, useIonAlert,
} from "@ionic/react";
import type { TrankMaterial, TrankMaterialKus } from "@routera/shared";
import { api } from "../lib/api";
import { BarcodeScanner } from "../components/BarcodeScanner";

type Rezim = "nalozit" | "vylozit";

export function TrankMaterialScreen() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const rezim: Rezim = params.get("kind") === "vylozit" ? "vylozit" : "nalozit";
  const rid = params.get("rid");
  const backUrl = rid ? `/route?rid=${rid}` : "/route";
  const qc = useQueryClient();
  const [presentAlert] = useIonAlert();

  // Nakládka: co řidič naskenoval. Vykládka: co v bedně NEBYLO.
  const [hotovo, setHotovo] = useState<string[]>([]);
  const [chybi, setChybi] = useState<string[]>([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, isError } = useQuery<TrankMaterial>({
    queryKey: ["trank", "material", id],
    queryFn: () => api.shift.trankMaterial(id),
  });

  const kusy: TrankMaterialKus[] = useMemo(
    () => (rezim === "nalozit" ? data?.k_nalozeni : data?.veze) ?? [],
    [data, rezim],
  );

  // Pečeť → kus. Řidič skenuje pečeti, ale předává se id zásilky.
  const podlePeceti = useMemo(() => {
    const m: Record<string, string> = {};
    kusy.forEach((k) => k.pecete.forEach((p) => { m[p] = k.id; }));
    return m;
  }, [kusy]);

  const handleScan = useCallback((kod: string) => {
    const zid = podlePeceti[kod];
    if (!zid) {
      // Neohlášená pečeť projde i u materiálu (rozhodnutí z 15. 8.) — jen nemá kus,
      // ke kterému by patřila, takže řidiče upozorníme a jedeme dál.
      presentAlert({
        header: "Neznámá pečeť",
        message: `${kod} k tomuhle kurzu nepatří. Naložit ji můžeš, ale nahlas to počítárně.`,
        buttons: ["OK"],
      });
      return;
    }
    setHotovo((p) => (p.includes(zid) ? p : [...p, zid]));
  }, [podlePeceti, presentAlert]);

  // Po načtení dat je při VYKLÁDCE výchozí stav „všechno sedí" — nic není chybějící.
  useEffect(() => { setChybi([]); setHotovo([]); }, [id, rezim]);

  const oznacenoChybi = (zid: string) => chybi.includes(zid);
  const toggleChybi = (zid: string) =>
    setChybi((p) => (p.includes(zid) ? p.filter((x) => x !== zid) : [...p, zid]));
  // Ruční zaškrtnutí ťuknutím = záloha pro nečitelnou pečeť (odsouhlasený návrh
  // z 15. 8.). Sken i ťuknutí vedou na totéž — přidání kusu do `hotovo`.
  const toggleHotovo = (zid: string) =>
    setHotovo((p) => (p.includes(zid) ? p.filter((x) => x !== zid) : [...p, zid]));

  const hotovoCount = rezim === "nalozit" ? hotovo.length : kusy.length - chybi.length;

  const dokoncit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (rezim === "nalozit") {
        await api.shift.trankMaterialNalozeno(id, hotovo);
      } else {
        await api.shift.trankMaterialVylozeno(id, kusy.map((k) => k.id), chybi);
      }
      await qc.invalidateQueries({ queryKey: ["trank", "material", id] });
      // ⚠️ Klíč trasy je ["mobile","route","today"] (viz TodayRouteScreen) — dřív se
      // invalidovalo ["route","today"], což nesedí na nic, takže hotový úkol
      // v seznamu zůstával, dokud nevypršel staleTime.
      await qc.invalidateQueries({ queryKey: ["mobile", "route", "today"] });
      history.replace(backUrl);
    } catch (e: any) {
      presentAlert({ header: "Chyba", message: e?.message ?? "Uložení selhalo", buttons: ["OK"] });
      setSaving(false);
    }
  };

  const nadpis = rezim === "nalozit" ? "Naložit materiál" : "Vyložit materiál";
  const tlacitko = kusy.length === 0
    ? "✓ Dokončit — žádný materiál"
    : rezim === "nalozit"
      ? "✓ Materiál naložen"
      : chybi.length
        ? `✓ Vyložit — ${chybi.length >= 2 && chybi.length <= 4 ? "pohřešují se" : "pohřešuje se"} ${chybi.length}`
        : "✓ Vyloženo — všechno sedí";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref={backUrl} /></IonButtons>
          <IonTitle>{nadpis}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
        ) : isError ? (
          <div style={{ padding: 24 }}>
            <p style={{ color: "rgb(var(--c-danger))" }}>Nepodařilo se načíst materiál.</p>
          </div>
        ) : (
          <div style={{ padding: "16px 12px" }}>

            {rezim === "vylozit" && (
              <div style={{
                background: "rgb(var(--c-accent) / 0.08)",
                border: "1px solid rgb(var(--c-accent) / 0.3)",
                borderRadius: 12, padding: "12px 14px", marginBottom: 12,
                fontSize: 13, lineHeight: 1.5, color: "rgb(var(--c-text))",
              }}>
                Potvrzením se materiál označí jako <b>doručený na depo {data?.depot ?? ""}</b>.
                Teprve pak ho místní řidiči uvidí v appce.
              </div>
            )}

            <div style={{
              background: "rgb(var(--c-surface))", border: "1px solid rgb(var(--c-border))",
              borderRadius: 12, padding: "14px 16px", marginBottom: 12,
            }}>
              <div style={{ fontSize: 13, color: "rgb(var(--c-muted))" }}>
                {rezim === "nalozit" ? "Naloženo" : "Předáno na depo"}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "rgb(var(--c-text))" }}>
                {hotovoCount}{rezim === "vylozit" && ` z ${kusy.length}`}
              </div>
              <div style={{ fontSize: 11, color: "rgb(var(--c-muted))", marginTop: 4, lineHeight: 1.45 }}>
                {rezim === "nalozit"
                  ? "Skenuj pečeti materiálu, nebo kus ťukni ručně. Co je naložené, zezelená."
                  : "Neskenuje se. Když všechno sedí, rovnou potvrď — kus, který v bedně není, ťukni."}
              </div>
            </div>

            {kusy.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "32px 16px", color: "rgb(var(--c-muted))", fontSize: 14,
              }}>
                {rezim === "nalozit"
                  ? "Pro tenhle kurz zatím žádný materiál nečeká."
                  : "Tenhle kurz žádný materiál neveze."}
              </div>
            ) : kusy.map((k) => {
              const zeleny = rezim === "nalozit" ? hotovo.includes(k.id) : !oznacenoChybi(k.id);
              const lost = rezim === "vylozit" && oznacenoChybi(k.id);
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={rezim === "vylozit" ? () => toggleChybi(k.id) : () => toggleHotovo(k.id)}
                  style={{
                    width: "100%", textAlign: "left", font: "inherit", marginBottom: 8,
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "11px 12px", borderRadius: 10,
                    background: lost ? "rgb(var(--c-warning) / 0.12)"
                      : zeleny ? "rgb(var(--c-success) / 0.12)" : "rgb(var(--c-surface))",
                    border: `1px solid ${lost ? "rgb(var(--c-warning) / 0.5)"
                      : zeleny ? "rgb(var(--c-success) / 0.4)" : "rgb(var(--c-border))"}`,
                    cursor: "pointer",
                  }}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, flex: "none",
                    display: "grid", placeItems: "center", fontSize: 13, color: "#fff",
                    background: lost ? "rgb(var(--c-warning))"
                      : zeleny ? "rgb(var(--c-success))" : "transparent",
                    border: zeleny || lost ? "none" : "1.5px solid rgb(var(--c-border-strong))",
                  }}>{lost ? "!" : zeleny ? "✓" : ""}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{
                      fontFamily: "monospace", fontSize: 13, fontWeight: 600,
                      color: "rgb(var(--c-text))",
                      textDecoration: lost ? "line-through" : "none",
                    }}>{k.pecete.join(", ") || `#${k.cislo}`}</span>
                    <br />
                    <span style={{ fontSize: 12, color: "rgb(var(--c-muted))" }}>
                      {k.prijemce || `${k.klient ?? ""} ${k.branch_id ?? ""}`.trim()}
                    </span>
                  </span>
                  {lost && (
                    <span style={{
                      marginLeft: "auto", flex: "none", fontSize: 10, fontWeight: 700,
                      padding: "2px 6px", borderRadius: 5,
                      background: "rgb(var(--c-warning) / 0.2)", color: "rgb(var(--c-warning))",
                    }}>pohřešuje se</span>
                  )}
                </button>
              );
            })}

            {rezim === "nalozit" && kusy.length > 0 && (
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                style={{
                  width: "100%", padding: "15px 20px", marginTop: 4, marginBottom: 10,
                  background: "rgb(var(--c-accent))", color: "#fff", border: "none",
                  borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer",
                }}
              >
                📷 Skenovat pečeť
              </button>
            )}

            {/* Dokončit jde VŽDY — i prázdný úkol (žádný materiál) musí jít odklik-
                nout, jinak by v seznamu visel napořád. Dokončení = rozhodnutí
                řidiče, server si ho zapíše (material_*_at) a úkol zmizí. */}
            <button
              type="button"
              onClick={dokoncit}
              disabled={saving}
              style={{
                width: "100%", padding: "16px 20px",
                background: chybi.length ? "rgb(var(--c-warning))" : "rgb(var(--c-success))",
                color: "#fff", border: "none", borderRadius: 11,
                fontSize: 15, fontWeight: 700,
                opacity: saving ? 0.5 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Ukládám…" : tlacitko}
            </button>

            {rezim === "nalozit" && hotovo.length < kusy.length && (
              <p style={{ fontSize: 12, color: "rgb(var(--c-muted))", marginTop: 10, lineHeight: 1.5 }}>
                Nenaložené kusy zůstanou v Brně a počítárna je přiřadí na jiný den.
                Chybí {kusy.length - hotovo.length}.
              </p>
            )}
            {rezim === "vylozit" && chybi.length > 0 && (
              <p style={{ fontSize: 12, color: "rgb(var(--c-muted))", marginTop: 10, lineHeight: 1.5 }}>
                Označené kusy dostanou stav <b>Pohřešuje se</b> — nejsou doručené, ale ani
                tiše nezmizí. Počítárna i dispečer je uvidí, dokud se nenajdou.
              </p>
            )}
          </div>
        )}

        {scannerOpen && (
          <BarcodeScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />
        )}
      </IonContent>
    </IonPage>
  );
}
